import type {Action} from '@github-ui/action-bar'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  getTokensByType,
  inspectFilterBranchesInSearchQuery,
  replaceAllFiltersByTypeInSearchQuery,
  replaceFilterBranchInSearchQuery,
  replaceTokensDifferentially,
  type TokenType,
} from '@github-ui/filter-query'
import {AssigneeRepositoryPicker} from '@github-ui/item-picker-assignee-relay'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {formatActorLogin} from '@github-ui/item-picker-assignee-tsq/utils/assignee-picker'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import type {ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import {IssueTypePicker} from '@github-ui/item-picker-issue-type-tsq'
import {LabelPicker, type LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {MilestonePicker} from '@github-ui/item-picker-milestone-tsq'
import type {ProjectPickerBaseProject as ProjectPickerProject} from '@github-ui/item-picker-project'
import {ProjectPicker} from '@github-ui/item-picker-project-tsq'
import {ListFilterButton} from '@github-ui/list-filter-button'
import type {ScopedRepository} from '@github-ui/list-view-items-issues-prs/Query'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {IssueOpenedIcon, MentionIcon, MilestoneIcon, PersonIcon, ProjectIcon, TagIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import type React from 'react'
import {type Dispatch, type SetStateAction, useCallback, useMemo, useState} from 'react'

import {LABELS} from './labels'
import styles from './repository-filter-actions.module.css'

export type RepositoryFilterPickerProps = {
  nested?: boolean
  repo: ScopedRepository
  applySectionFilter: (query: string) => void
  query: string
  structuralUpdatesEnabled: boolean
}

export function replaceRepositoryFilterInQuery(
  query: string,
  tokens: string[],
  type: TokenType,
  options?: Parameters<typeof replaceFilterBranchInSearchQuery>[3],
) {
  return replaceFilterBranchInSearchQuery(query, tokens, type, {
    replaceMultipleFlatBranches: true,
    reconcilePositiveBranchSubset: type === 'label',
    ...options,
  })
}

type RepositoryFilterReplacementOptions = Parameters<typeof replaceFilterBranchInSearchQuery>[3] & {
  legacyIsNegatedFilter?: boolean
}

export function replaceRepositoryFilterForSurfaceInQuery(
  query: string,
  tokens: string[],
  type: TokenType,
  structuralUpdatesEnabled: boolean,
  options?: RepositoryFilterReplacementOptions,
) {
  const {legacyIsNegatedFilter, ...structuralOptions} = options ?? {}
  if (structuralUpdatesEnabled) return replaceRepositoryFilterInQuery(query, tokens, type, structuralOptions)
  if (type === 'author' || type === 'milestone') {
    return replaceAllFiltersByTypeInSearchQuery(query, tokens, type, legacyIsNegatedFilter)
  }
  return replaceTokensDifferentially(query, tokens, type)
}

function applyRepositoryFilterChange(
  query: string,
  nextQuery: string,
  applySectionFilter: RepositoryFilterPickerProps['applySectionFilter'],
  applyWhenUnchanged = false,
) {
  if (!applyWhenUnchanged && nextQuery === query) return false
  applySectionFilter(nextQuery)
  return true
}

function hasRepositoryNoValueFilter(
  query: string,
  type: 'milestone' | 'assignee' | 'type',
  structuralUpdatesEnabled: boolean,
) {
  if (!structuralUpdatesEnabled) return query.includes(`no:${type}`)

  const inspection = inspectFilterBranchesInSearchQuery(query, [type])
  if (!inspection.isSafe) return false

  const hasNoValueBranch = inspection.branches.some(
    branch =>
      !branch.isNegated &&
      branch.key === 'no' &&
      branch.values.length === 1 &&
      branch.values[0]?.toLowerCase() === type,
  )
  if (!hasNoValueBranch) return false

  return (
    inspection.branches.every(branch => !branch.isNegated && branch.key === 'no') &&
    (!inspection.hasBooleanStructure || inspection.branches.length === 1)
  )
}

export function getRepositoryFilterPickerTokens(query: string, type: TokenType) {
  const inspection = inspectFilterBranchesInSearchQuery(query, [type])
  if (!inspection.isSafe) return []
  if (
    inspection.branches.some(branch => branch.isNegated || branch.key !== type) ||
    (inspection.hasBooleanStructure && inspection.branches.length > 1)
  ) {
    return []
  }
  return inspection.branches.flatMap(branch => branch.values)
}

export function getRepositoryLabelPickerState(query: string): {tokens: string[]; hasNoValue: boolean} {
  const inspection = inspectFilterBranchesInSearchQuery(query, ['label'])
  if (!inspection.isSafe) return {tokens: [], hasNoValue: false}

  const tokens: string[] = []
  const identities = new Set<string>()
  let hasNoValue = false
  for (const branch of inspection.branches) {
    if (branch.isNegated) continue
    if (branch.key === 'no') {
      if (identities.has('no-value:label')) return {tokens: [], hasNoValue: false}
      identities.add('no-value:label')
      hasNoValue = true
      continue
    }
    for (const value of branch.values) {
      const identity = `value:${value.toLowerCase()}`
      if (identities.has(identity)) return {tokens: [], hasNoValue: false}
      identities.add(identity)
      tokens.push(value)
    }
  }

  return {tokens, hasNoValue}
}

function getRepositoryFilterPickerTokensForSurface(query: string, type: TokenType, structuralUpdatesEnabled: boolean) {
  return structuralUpdatesEnabled ? getRepositoryFilterPickerTokens(query, type) : getTokensByType(query, type)
}

function getRepositoryLabelPickerStateForSurface(query: string, structuralUpdatesEnabled: boolean) {
  return structuralUpdatesEnabled
    ? getRepositoryLabelPickerState(query)
    : {tokens: getTokensByType(query, 'label'), hasNoValue: query.includes('no:label')}
}

type RepositoryFilterActionsProps = {
  repo: ScopedRepository | undefined
  applySectionFilter: RepositoryFilterPickerProps['applySectionFilter']
  issueTypesEnabled: boolean
  query: string
}

export function useRepositoryFilterActions({
  repo,
  applySectionFilter,
  issueTypesEnabled,
  query,
}: RepositoryFilterActionsProps): Action[] {
  const structuralUpdatesEnabled = isFeatureEnabled('issues_structural_quick_filter_updates')
  return useMemo(() => {
    if (!repo) return []

    const pickerProps = {repo, applySectionFilter, query, structuralUpdatesEnabled}
    const actions: Action[] = [
      {key: 'authors', render: nested => <RepositoryAuthorFilter {...pickerProps} nested={nested} />},
      {key: 'labels', render: nested => <RepositoryLabelFilter {...pickerProps} nested={nested} />},
      {key: 'projects', render: nested => <RepositoryProjectFilter {...pickerProps} nested={nested} />},
      {key: 'milestones', render: nested => <RepositoryMilestoneFilter {...pickerProps} nested={nested} />},
      {key: 'assignees', render: nested => <RepositoryAssigneeFilter {...pickerProps} nested={nested} />},
    ]

    if (issueTypesEnabled) {
      actions.push({
        key: 'issue-types',
        render: nested => <RepositoryIssueTypeFilter {...pickerProps} nested={nested} />,
      })
    }

    return actions
  }, [applySectionFilter, issueTypesEnabled, query, repo, structuralUpdatesEnabled])
}

export function RepositoryAuthorFilter({
  repo: {name: repo, owner},
  applySectionFilter,
  nested,
  query,
  structuralUpdatesEnabled,
}: RepositoryFilterPickerProps) {
  const currentAuthorTokens = useMemo(
    () => getRepositoryFilterPickerTokensForSurface(query, 'author', structuralUpdatesEnabled),
    [query, structuralUpdatesEnabled],
  )
  const includeBots = isFeatureEnabled('issues_react_include_bots_in_pickers')
  const onSelectionChanged = useCallback(
    (selectedAuthors: AssigneePickerAssignee[]) => {
      const authors = selectedAuthors.map(formatActorLogin)
      applyRepositoryFilterChange(
        query,
        replaceRepositoryFilterForSurfaceInQuery(query, authors, 'author', structuralUpdatesEnabled),
        applySectionFilter,
        !structuralUpdatesEnabled,
      )
    },
    [applySectionFilter, query, structuralUpdatesEnabled],
  )

  return (
    <AssigneeRepositoryPicker
      name="author"
      metricsSurface="issues_list_filter"
      selectionVariant="single"
      readonly={false}
      title={LABELS.filters.authorLabel}
      assigneeTokens={currentAuthorTokens}
      assignees={[]}
      repo={repo}
      owner={owner}
      onSelectionChange={onSelectionChanged}
      anchorElement={nested ? NestedAuthorsAnchor : AuthorsAnchor}
      showNoMatchItem
      includeAuthorableBots={includeBots}
      includeAssignableBots={false}
    />
  )
}

export function RepositoryLabelFilter({
  nested,
  repo,
  applySectionFilter,
  query,
  structuralUpdatesEnabled,
}: RepositoryFilterPickerProps) {
  const labelPickerState = useMemo(
    () => getRepositoryLabelPickerStateForSurface(query, structuralUpdatesEnabled),
    [query, structuralUpdatesEnabled],
  )
  const hasNoLabelFilter = labelPickerState.hasNoValue
  const noLabelItem = useMemo<ExtendedItemProps<LabelPickerLabel>>(
    () => ({
      id: SPECIAL_VALUES.noLabelsData.id,
      description: '',
      descriptionVariant: 'inline',
      children: (
        <VerifiedHTMLText
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
          html={SPECIAL_VALUES.noLabelsData.name as SafeHTMLString}
          title={SPECIAL_VALUES.noLabelsData.description}
        />
      ),
      source: SPECIAL_VALUES.noLabelsData as LabelPickerLabel,
      selected: hasNoLabelFilter,
      groupId: '',
      leadingVisual: () => <div className={styles.LabelColorIndicator} />,
    }),
    [hasNoLabelFilter],
  )
  const currentLabelTokens = labelPickerState.tokens.length > 0 ? labelPickerState.tokens : undefined
  const onSelectionChanged = useCallback(
    (selectedLabels: LabelPickerLabel[]) => {
      if (!structuralUpdatesEnabled) {
        const labels = selectedLabels.map(label =>
          label.id === SPECIAL_VALUES.noLabelsData.id ? 'no:label' : label.name,
        )
        applyRepositoryFilterChange(
          query,
          replaceRepositoryFilterForSurfaceInQuery(query, labels, 'label', false),
          applySectionFilter,
          true,
        )
        return
      }
      const syntheticNoLabel = selectedLabels.find(label => label.id === SPECIAL_VALUES.noLabelsData.id)
      const realLabels = selectedLabels.filter(label => label.id !== SPECIAL_VALUES.noLabelsData.id)
      const labelsToApply =
        syntheticNoLabel && realLabels.length > 0
          ? hasNoLabelFilter
            ? realLabels
            : [syntheticNoLabel]
          : selectedLabels
      const hasLiteralNoLabel = labelsToApply.some(
        label => label.id !== SPECIAL_VALUES.noLabelsData.id && label.name.toLowerCase() === 'no:label',
      )
      const labels = labelsToApply.map(label => (label.id === SPECIAL_VALUES.noLabelsData.id ? 'no:label' : label.name))
      const nextQuery = replaceRepositoryFilterForSurfaceInQuery(query, labels, 'label', true, {
        treatNoValueAsLiteral: hasLiteralNoLabel,
      })
      applyRepositoryFilterChange(query, nextQuery, applySectionFilter)
    },
    [applySectionFilter, hasNoLabelFilter, query, structuralUpdatesEnabled],
  )

  return (
    <LabelPicker
      anchorElement={nested ? NestedLabelsAnchor : LabelsAnchor}
      title={LABELS.filters.labelsLabel}
      showEditLabelsButton={false}
      repo={repo.name}
      owner={repo.owner}
      readonly={false}
      onSelectionChange={onSelectionChanged}
      noLabelOption={noLabelItem}
      labels={[]}
      labelNames={currentLabelTokens}
      canCreateLabel={false}
      enableSuggestedLabels
      includeArchived
    />
  )
}

export function RepositoryProjectFilter({
  repo,
  applySectionFilter,
  nested,
  query,
  structuralUpdatesEnabled,
}: RepositoryFilterPickerProps) {
  const currentProjectsTokens = useMemo(
    () => getRepositoryFilterPickerTokensForSurface(query, 'project', structuralUpdatesEnabled),
    [query, structuralUpdatesEnabled],
  )
  const onSelectionChanged = useCallback(
    (selectedProjects: ProjectPickerProject[]) => {
      const projects = selectedProjects.map(project => `${repo.owner}/${project.number}`)
      const newQuery = replaceRepositoryFilterForSurfaceInQuery(query, projects, 'project', structuralUpdatesEnabled)
      applyRepositoryFilterChange(query, newQuery, applySectionFilter)
    },
    [applySectionFilter, query, repo.owner, structuralUpdatesEnabled],
  )
  const currentSelectedProjects = useCallback(
    (projects: ProjectPickerProject[]) => {
      const selectedProjects: ProjectPickerProject[] = []
      for (const token of currentProjectsTokens) {
        const foundProject = projects.find(project => {
          const [repoName, number] = token.split('/')
          return project.url.endsWith(`/${repoName}/projects/${number}`)
        })
        if (foundProject?.id && !selectedProjects.includes(foundProject)) selectedProjects.push(foundProject)
      }
      return selectedProjects
    },
    [currentProjectsTokens],
  )

  return (
    <ProjectPicker
      pickerId="list-project-filter"
      anchorElement={nested ? NestedProjectsAnchor : ProjectsAnchor}
      repo={repo.name}
      owner={repo.owner}
      onSave={onSelectionChanged}
      selectedProjects={[]}
      readonly={false}
      getSelectedProjects={currentSelectedProjects}
      title={LABELS.filters.projectsLabel}
    />
  )
}

export function RepositoryMilestoneFilter({
  nested,
  repo,
  applySectionFilter,
  query,
  structuralUpdatesEnabled,
}: RepositoryFilterPickerProps) {
  const noMilestoneItem = useMemo<ExtendedItemProps<MilestonePickerMilestone>>(
    () => ({
      id: SPECIAL_VALUES.noMilestoneData.id,
      description: '',
      descriptionVariant: 'inline',
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
      children: <VerifiedHTMLText html={SPECIAL_VALUES.noMilestoneData.title as SafeHTMLString} />,
      source: SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestone,
      groupId: '',
      selected: hasRepositoryNoValueFilter(query, 'milestone', structuralUpdatesEnabled),
      leadingVisual: () => <MilestoneIcon />,
    }),
    [query, structuralUpdatesEnabled],
  )
  const currentMilestoneTokens = useMemo(
    () => getRepositoryFilterPickerTokensForSurface(query, 'milestone', structuralUpdatesEnabled),
    [query, structuralUpdatesEnabled],
  )
  const [activeMilestone, setActiveMilestone] = useActiveMilestone(currentMilestoneTokens)
  const onSelectionChanged = useCallback(
    (selectedMilestones: MilestonePickerMilestone[]) => {
      const selectedMilestone = selectedMilestones[0]
      const isNoMilestone = selectedMilestone?.id === SPECIAL_VALUES.noMilestoneData.id
      const nextQuery = replaceRepositoryFilterForSurfaceInQuery(
        query,
        selectedMilestones.map(milestone =>
          structuralUpdatesEnabled && milestone.id === SPECIAL_VALUES.noMilestoneData.id
            ? 'no:milestone'
            : milestone.title,
        ),
        'milestone',
        structuralUpdatesEnabled,
        {
          treatNoValueAsLiteral: selectedMilestone?.title.toLowerCase() === 'no:milestone' && !isNoMilestone,
          legacyIsNegatedFilter: isNoMilestone,
        },
      )
      if (!structuralUpdatesEnabled) {
        if (selectedMilestone) setActiveMilestone(selectedMilestone)
        applyRepositoryFilterChange(query, nextQuery, applySectionFilter, true)
        return
      }
      const selectedMilestoneMatchesQuery =
        selectedMilestone &&
        currentMilestoneTokens.length === 1 &&
        currentMilestoneTokens[0]?.toLowerCase() === selectedMilestone.title.toLowerCase()
      if (selectedMilestone && (nextQuery !== query || selectedMilestoneMatchesQuery)) {
        setActiveMilestone(selectedMilestone)
      }
      applyRepositoryFilterChange(query, nextQuery, applySectionFilter)
    },
    [applySectionFilter, currentMilestoneTokens, query, setActiveMilestone, structuralUpdatesEnabled],
  )

  return (
    <MilestonePicker
      repo={repo.name}
      activeMilestone={activeMilestone}
      anchorElement={nested ? NestedMilestonesAnchor : MilestonesAnchor}
      owner={repo.owner}
      onSelectionChange={onSelectionChanged}
      noMilestoneItem={noMilestoneItem}
      title={LABELS.filters.milestonesLabel}
      canCreateMilestone={false}
    />
  )
}

export function RepositoryAssigneeFilter({
  repo: {name: repo, owner},
  applySectionFilter,
  nested,
  query,
  structuralUpdatesEnabled,
}: RepositoryFilterPickerProps) {
  const currentAssigneeTokens = useMemo(
    () => getRepositoryFilterPickerTokensForSurface(query, 'assignee', structuralUpdatesEnabled).slice(-1),
    [query, structuralUpdatesEnabled],
  )
  const noAssigneeOption = useMemo<ExtendedItemProps<AssigneePickerAssignee>>(
    () => ({
      id: SPECIAL_VALUES.noAssigneeData.id,
      description: '',
      descriptionVariant: 'inline',
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
      children: <VerifiedHTMLText html={SPECIAL_VALUES.noAssigneeData.login as SafeHTMLString} />,
      source: SPECIAL_VALUES.noAssigneeData as AssigneePickerAssignee,
      selected: hasRepositoryNoValueFilter(query, 'assignee', structuralUpdatesEnabled),
      leadingVisual: () => <PersonIcon />,
    }),
    [query, structuralUpdatesEnabled],
  )
  const onSelectionChanged = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      const assignees = selectedAssignees.map(assignee =>
        assignee.id === SPECIAL_VALUES.noAssigneeData.id ? 'no:assignee' : formatActorLogin(assignee),
      )
      applyRepositoryFilterChange(
        query,
        replaceRepositoryFilterForSurfaceInQuery(query, assignees, 'assignee', structuralUpdatesEnabled),
        applySectionFilter,
        !structuralUpdatesEnabled,
      )
    },
    [applySectionFilter, query, structuralUpdatesEnabled],
  )

  return (
    <AssigneeRepositoryPicker
      name="assignee"
      metricsSurface="issues_list_filter"
      selectionVariant="single"
      readonly={false}
      title={LABELS.filters.assigneesLabel}
      assigneeTokens={currentAssigneeTokens}
      assignees={[]}
      repo={repo}
      owner={owner}
      onSelectionChange={onSelectionChanged}
      noAssigneeOption={noAssigneeOption}
      anchorElement={nested ? NestedAssigneesAnchor : AssigneesAnchor}
      showNoMatchItem
      includeAuthorableBots={false}
      includeAssignableBots
    />
  )
}

export function RepositoryIssueTypeFilter({
  repo: {owner, name: repo},
  applySectionFilter,
  nested,
  query,
  structuralUpdatesEnabled,
}: RepositoryFilterPickerProps) {
  const noIssueTypeItem = useMemo<ExtendedItemProps<IssueTypePickerIssueType>>(
    () => ({
      id: SPECIAL_VALUES.noTypeData.id,
      description: '',
      descriptionVariant: 'inline',
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
      children: <VerifiedHTMLText html={SPECIAL_VALUES.noTypeData.name as SafeHTMLString} />,
      source: SPECIAL_VALUES.noTypeData as IssueTypePickerIssueType,
      groupId: '',
      selected: hasRepositoryNoValueFilter(query, 'type', structuralUpdatesEnabled),
    }),
    [query, structuralUpdatesEnabled],
  )
  const currentTypeToken = useMemo(
    () => getRepositoryFilterPickerTokensForSurface(query, 'type', structuralUpdatesEnabled).at(-1),
    [query, structuralUpdatesEnabled],
  )
  const onSelectionChanged = useCallback(
    (issueTypes: IssueTypePickerIssueType[] | null) => {
      const tokens =
        issueTypes?.map(issueType => (issueType.id === SPECIAL_VALUES.noTypeData.id ? 'no:type' : issueType.name)) ?? []
      applyRepositoryFilterChange(
        query,
        replaceRepositoryFilterForSurfaceInQuery(query, tokens, 'type', structuralUpdatesEnabled),
        applySectionFilter,
        !structuralUpdatesEnabled,
      )
    },
    [applySectionFilter, query, structuralUpdatesEnabled],
  )

  return (
    <IssueTypePicker
      title={LABELS.filters.issueTypeLabel}
      owner={owner}
      repo={repo}
      readonly={false}
      activeIssueType={null}
      issueTypeToken={currentTypeToken}
      onSelectionChange={onSelectionChanged}
      anchorElement={nested ? NestedIssueTypeAnchor : IssueTypeAnchor}
      width="medium"
      noTypeItem={noIssueTypeItem}
    />
  )
}

function useActiveMilestone(
  currentMilestoneTokens: readonly unknown[],
): [MilestonePickerMilestone | null, Dispatch<SetStateAction<MilestonePickerMilestone | null>>] {
  const [activeMilestone, setActiveMilestone] = useState<MilestonePickerMilestone | null>(null)
  const [previousTokens, setPreviousTokens] = useState(currentMilestoneTokens)
  if (currentMilestoneTokens !== previousTokens) {
    setPreviousTokens(currentMilestoneTokens)
    if (currentMilestoneTokens.length === 0 && activeMilestone !== null) {
      setActiveMilestone(null)
      return [null, setActiveMilestone]
    }
  }
  return [activeMilestone, setActiveMilestone]
}

function FilterButton({
  testId,
  label,
  ariaLabel,
  'aria-label': providedAriaLabel,
  'aria-expanded': ariaExpanded = false,
  'aria-haspopup': ariaHasPopup = 'dialog',
  ...props
}: React.HTMLAttributes<HTMLButtonElement> & {testId: string; label: string; ariaLabel?: string}) {
  return (
    <ListFilterButton
      {...props}
      data-testid={testId}
      aria-label={providedAriaLabel ?? ariaLabel ?? `Filter by ${label.toLowerCase()}`}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
    >
      {label}
    </ListFilterButton>
  )
}

function NestedFilterAnchor({
  label,
  ariaLabel,
  icon: Icon,
  ...props
}: React.HTMLAttributes<HTMLElement> & {label: string; ariaLabel?: string; icon: React.ComponentType}) {
  return (
    <ActionList.Item {...props} aria-label={ariaLabel ?? `Filter by ${label.toLowerCase()}`} role="menuitem">
      <ActionList.LeadingVisual>
        <Icon />
      </ActionList.LeadingVisual>
      {label}...
    </ActionList.Item>
  )
}

const AuthorsAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton {...props} testId="authors-anchor-button" label={LABELS.filters.author} />
)
const LabelsAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton {...props} testId="labels-anchor-button" label={LABELS.filters.labels} />
)
const ProjectsAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton {...props} testId="projects-anchor-button" label={LABELS.filters.projects} />
)
const MilestonesAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton {...props} testId="milestones-anchor-button" label={LABELS.filters.milestones} />
)
const AssigneesAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton {...props} testId="assignees-anchor-button" label={LABELS.filters.assignees} />
)
const IssueTypeAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton
    {...props}
    testId="issue-types-anchor-button"
    label={LABELS.filters.issueType}
    ariaLabel={LABELS.filters.issueTypeLabel}
  />
)
const NestedAuthorsAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor {...props} label={LABELS.filters.author} icon={PersonIcon} />
)
const NestedLabelsAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor {...props} label={LABELS.filters.labels} icon={TagIcon} />
)
const NestedProjectsAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor {...props} label={LABELS.filters.projects} icon={ProjectIcon} />
)
const NestedMilestonesAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor {...props} label={LABELS.filters.milestones} icon={MilestoneIcon} />
)
const NestedAssigneesAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor {...props} label={LABELS.filters.assignees} icon={MentionIcon} />
)
const NestedIssueTypeAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor
    {...props}
    label={LABELS.filters.issueType}
    ariaLabel={LABELS.filters.issueTypeLabel}
    icon={IssueOpenedIcon}
  />
)
